/**
 * Shared FormCore normalizer — used by both useDynamicFormStore and
 * useDynamicReviewTaskStore to apply canonical defaults to loaded data.
 *
 * Strict rules:
 *  - Only canonical field types are preserved (no image_upload/video_upload/audio_upload).
 *  - Only strict per-type properties are kept on each field.
 *  - accessMode and responseMode are uppercase-only.
 */

const VALID_FIELD_TYPES = new Set([
  'text', 'number', 'date', 'time', 'dropdown', 'radio', 'checkbox',
  'display', 'file_upload', 'rich_text', 'list', 'repeatable_section',
]);

const COMMON_KEYS = ['_id', 'key', 'label', 'type', 'order', 'helpText'];

const TYPE_ALLOWED_KEYS = {
  text:               [...COMMON_KEYS, 'required', 'placeholder', 'defaultValue', 'multiline', 'validations'],
  number:             [...COMMON_KEYS, 'required', 'placeholder', 'defaultValue', 'validations'],
  date:               [...COMMON_KEYS, 'required', 'placeholder', 'defaultValue', 'validations'],
  time:               [...COMMON_KEYS, 'required', 'placeholder', 'defaultValue', 'validations'],
  dropdown:           [...COMMON_KEYS, 'required', 'options', 'validations', 'optionsSource', 'databaseId', 'labelColumnId', 'valueColumnId', 'filters'],
  radio:              [...COMMON_KEYS, 'required', 'options', 'validations', 'optionsSource', 'databaseId', 'labelColumnId', 'valueColumnId', 'filters'],
  checkbox:           [...COMMON_KEYS, 'required', 'options', 'validations', 'optionsSource', 'databaseId', 'labelColumnId', 'valueColumnId', 'filters'],
  display:            [...COMMON_KEYS, 'content', 'contentFormat'],
  file_upload:        [...COMMON_KEYS, 'required', 'upload', 'validations'],
  rich_text:          [...COMMON_KEYS, 'required', 'placeholder', 'defaultValue', 'validations'],
  list:               [...COMMON_KEYS, 'required', 'placeholder', 'listConfig', 'validations'],
  repeatable_section: [...COMMON_KEYS, 'children', 'minRepetitions', 'maxRepetitions'],
};

function stripField(field) {
  const type = field?.type;
  if (!type || !VALID_FIELD_TYPES.has(type)) return null;
  const allowed = new Set(TYPE_ALLOWED_KEYS[type] || COMMON_KEYS);
  const cleaned = {};
  for (const k of Object.keys(field)) {
    if (allowed.has(k)) cleaned[k] = field[k];
  }
  if (!cleaned._id) {
    cleaned._id = crypto.randomUUID();
  }
  if (type === 'repeatable_section' && Array.isArray(cleaned.children)) {
    cleaned.children = cleaned.children.map(stripField).filter(Boolean);
  }
  return cleaned;
}

/**
 * Normalize FormCore data loaded from the backend.
 * Returns a new object with strict canonical shapes.
 */
export function normalizeFormCore(rawData) {
  const data = { ...rawData };
  // Strip invalid field types and per-type irrelevant properties
  if (Array.isArray(data.fields)) {
    data.fields = data.fields.map(stripField).filter(Boolean);
  } else {
    data.fields = [];
  }
  if (data.accessPolicy && typeof data.accessPolicy === 'object') {
    if (data.accessPolicy.mode) data.accessPolicy.mode = data.accessPolicy.mode.toUpperCase();
    if (!Array.isArray(data.accessPolicy.selectedUserIds)) data.accessPolicy.selectedUserIds = [];
  } else if (typeof data.accessMode === 'string') {
    data.accessMode = data.accessMode.toUpperCase();
  }
  if (data.responseMode) data.responseMode = data.responseMode.toUpperCase();
  data.formSettings = data.formSettings || {};
  return data;
}

export { VALID_FIELD_TYPES, TYPE_ALLOWED_KEYS, stripField };
